// Turns the dispatch board's two data sources into the SIGNALWORKS state
// vocabulary (ui/src/app/components/lw/state.js). This file owns no colour,
// no plate shape, and no label text — it only decides WHICH value name from
// that vocabulary a row is in, from the raw API shapes. Pure: no Vue, no
// network, no implicit clock (the one place time enters is the explicit `now`
// argument, defaulted to `Date.now()` so callers never have to pass it in
// production but tests never have to fight it either).
//
// Two sources, and they disagree about a load in an important way
// (refresh_plan.md section 1: custody is the noun, telemetry is the adverb):
//
//   `GET /api/loads` — the board's own fetch. Has `delivered` (a dispatcher's
//   flag) but no custody chain and no telemetry at all.
//
//   `GET /api/tracking/live` — the dispatcher tracking read the live map
//   already uses. `{ devices, loads, coverage, stale_after_ms }`, where
//   `loads` has one entry per load STILL ACTIVE at the time of the fetch,
//   shaped `{ load_id, custody: { state, source_grade, since, until,
//   position: { basis, stale, as_of, latitude, longitude, ... }, carriers,
//   anomalies } }` (CustodyService.js / TrackingController.js).
//
// The second fetch is OPTIONAL enrichment: it can arrive late, fail, or
// simply not contain a given load (a delivered load ages out of "active").
// Every function below has to stay honest in that gap rather than guess, so
// "we do not know" is always a real, distinct value — never a silently
// reused default.

/**
 * Custody values suppressed from telemetry (custody is closed or has not
 * opened, so tracker freshness is not a question anyone can act on) — and,
 * for `delivered_unconfirmed` specifically, the mechanism that enforces the
 * rule state.js states in its comment on that value: never render a
 * telemetry warning plate on the same row as the delivered_unconfirmed
 * custody amber, or the two ambers read as one alarm. Exported so the board
 * and this file's tests read the same list rather than two copies drifting.
 */
export const TELEMETRY_SUPPRESSED_CUSTODY = Object.freeze([
  'delivered_unconfirmed',
  'delivered_confirmed',
  'awaiting_pickup',
  'cancelled',
]);

/** The `custody.state` strings CustodyService is known to emit. */
const RECOGNISED_CUSTODY_STATES = new Set([
  'awaiting_pickup',
  'in_transit',
  'delivered',
  'unknown',
]);

// `loads.delivered` comes back as a boolean, 0/1, or '0'/'1' depending on the
// row's path through the API, and only an explicit false/0/'0'/null means
// "not delivered".
function isDeliveredFlagSet(load) {
  const flag = load && load.delivered;
  return flag === true || flag === 1 || flag === '1';
}

/**
 * custodyForLoad(load, entry) -> a custody value name from state.js.
 *
 * `load` is a row from `GET /api/loads`. `entry` is this load's element from
 * `GET /api/tracking/live`'s `loads` array (or null/undefined when the load
 * is not in it, the fetch has not landed yet, or it failed).
 */
export function custodyForLoad(load, entry) {
  const custody = entry && entry.custody;
  const state = custody && custody.state;

  if (typeof state === 'string') {
    if (RECOGNISED_CUSTODY_STATES.has(state)) {
      if (state === 'delivered') {
        // driver_attested: the driver signed on the phone and nobody at the
        // office has countersigned it yet, which is exactly the fact
        // delivered_unconfirmed exists to carry. dispatcher_confirmed and
        // dispatch_flag both mean an office human is the source, so both
        // read as confirmed.
        return custody.source_grade === 'driver_attested'
          ? 'delivered_unconfirmed'
          : 'delivered_confirmed';
      }
      return state; // 'awaiting_pickup' | 'in_transit' | 'unknown'
    }
    // A server state this file does not recognise is not a plate we may
    // invent. This is a live entry saying something specific, so it does not
    // fall through to the loads.delivered flag below — that fallback is only
    // for when tracking/live said nothing at all.
    return 'unknown';
  }

  if (isDeliveredFlagSet(load)) {
    // No usable custody entry, but loads.delivered is set. That flag is
    // written by a dispatcher at the office, and CustodyService's own
    // authority table treats it as real under the grade 'dispatch_flag'
    // ("a dispatcher marking a load delivered the old way IS an authority" —
    // CustodyService.js) even with no drop completion behind it. Reporting
    // delivered_confirmed here restates an authority the API already grants;
    // it is not this file guessing ahead of the API.
    return 'delivered_confirmed';
  }

  return 'unknown';
}

/**
 * telemetryForLoad({ custody, entry, staleAfterMs, now }) -> null, or
 * `{ value, ageLabel, asOf }` where `value` is a telemetry value name from
 * state.js.
 *
 * `custody` is the value already produced by custodyForLoad (or, for a
 * cancelled load, whatever this board's cancellation source says — this
 * function does not derive custody itself, it only reacts to it).
 */
export function telemetryForLoad({ custody, entry, staleAfterMs, now = Date.now() } = {}) {
  if (TELEMETRY_SUPPRESSED_CUSTODY.includes(custody)) {
    // Custody is closed (delivered_unconfirmed, delivered_confirmed,
    // cancelled) or has not opened (awaiting_pickup): tracker freshness is
    // not a question anyone can act on for this row. Returning null here —
    // no telemetry plate at all, not `unknown` — is also what structurally
    // keeps a warning plate off a delivered_unconfirmed row, per the amber
    // rule in state.js's comment on that value.
    return null;
  }

  const position = entry && entry.custody && entry.custody.position;

  // No usable entry: the tracking/live fetch has not answered for this load
  // (not yet arrived, failed, or the load is simply not in it — the fetch is
  // OPTIONAL enrichment). custody === 'unknown' collapses to the same
  // "haven't asked" state even when an entry did arrive, because a custody
  // value this file could not classify is not a basis for reading position
  // data either. state.js's label for `unknown`, "Not asked", is the literal
  // truth in both cases, not a downgrade of some other kind of unknown.
  if (!entry || !position || custody === 'unknown') {
    return { value: 'unknown', ageLabel: null, asOf: null };
  }

  if (position.basis !== 'device_fix') {
    // In transit and we cannot see it. The same set the API's own coverage
    // block calls in_transit_untracked_load_ids (TrackingController.js) —
    // drop_completion, drop_stop, and pickup_stop are all inferred, not a
    // tracker reporting.
    return { value: 'untracked', ageLabel: null, asOf: null };
  }

  const asOf = position.as_of;
  const parsedAsOf = typeof asOf === 'string' ? Date.parse(asOf) : NaN;
  const stale =
    position.stale === true ||
    (typeof staleAfterMs === 'number' &&
      !Number.isNaN(parsedAsOf) &&
      now - parsedAsOf > staleAfterMs);

  return {
    value: stale ? 'stale' : 'live',
    ageLabel: relativeAge(asOf, now),
    asOf: asOf == null ? null : asOf,
  };
}

/**
 * relativeAge(iso, now) -> a short human string, or null for a missing or
 * unparseable date. `now` defaults to `Date.now()` so production call sites
 * never have to pass it; tests always do, so the bands below are exact
 * rather than racing the clock.
 */
export function relativeAge(iso, now = Date.now()) {
  if (iso == null) return null;
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return null;

  const diffMs = now - parsed;
  // A future timestamp is clock skew between this browser and whatever wrote
  // the fix, not a load that will be seen in -3 minutes. Clamp rather than
  // print a negative number.
  if (diffMs <= 0) return 'just now';

  const diffSec = diffMs / 1000;
  if (diffSec < 60) return 'just now';

  const diffMin = diffSec / 60;
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;

  const diffHour = diffMin / 60;
  if (diffHour < 48) return `${Math.floor(diffHour)}h ago`;

  const diffDay = diffHour / 24;
  return `${Math.floor(diffDay)}d ago`;
}
